import React, { useState, useMemo } from 'react';
import { Flame } from 'lucide-react';
import { Card, CardHeader, ProgressBar } from '../shared';
import { useTheme } from '../../contexts/ThemeContext';
import { formatCurrency } from '../../utils/formatters';
import { fireResults } from '../../utils/calculators';

interface FIRECalculatorProps {
  variant?: 'card' | 'page';
}

export const FIRECalculator: React.FC<FIRECalculatorProps> = ({ variant = 'card' }) => {
  const { darkMode } = useTheme();

  const [annualExpenses, setAnnualExpenses] = useState(0);
  const [investedAssets, setInvestedAssets] = useState(0);
  const [annualSavings, setAnnualSavings] = useState(0);
  const [returnRate, setReturnRate] = useState(0);
  const [swr, setSwr] = useState(0);

  const inputClass = `w-full px-3 py-2 rounded-lg text-sm ${
    darkMode
      ? 'bg-white/10 border border-white/10 text-white placeholder-slate-500'
      : 'bg-slate-50 border border-slate-200 text-slate-900 placeholder-slate-400'
  }`;

  const labelClass = `text-xs font-medium ${darkMode ? 'text-slate-400' : 'text-slate-500'}`;

  const results = useMemo(
    () => fireResults({ annualExpenses, investedAssets, annualSavings, returnRate, swr }),
    [annualExpenses, investedAssets, annualSavings, returnRate, swr],
  );

  return (
    <Card variant={variant}>
      <CardHeader
        title="FIRE Calculator"
        icon={<Flame size={20} />}
        variant={variant}
      />
      <div className="space-y-4">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <div>
            <label className={labelClass}>Annual Expenses</label>
            <input type="number" value={annualExpenses} onChange={e => setAnnualExpenses(+e.target.value)} onFocus={e => e.target.select()} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Invested Assets</label>
            <input type="number" value={investedAssets} onChange={e => setInvestedAssets(+e.target.value)} onFocus={e => e.target.select()} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Annual Savings</label>
            <input type="number" value={annualSavings} onChange={e => setAnnualSavings(+e.target.value)} onFocus={e => e.target.select()} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Return Rate (%)</label>
            <input type="number" value={returnRate} step={0.5} onChange={e => setReturnRate(+e.target.value)} onFocus={e => e.target.select()} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Withdrawal Rate (%)</label>
            <input type="number" value={swr} step={0.5} onChange={e => setSwr(+e.target.value)} onFocus={e => e.target.select()} className={inputClass} />
          </div>
        </div>

        {/* FIRE Number */}
        <div className={`rounded-xl p-4 space-y-3 ${darkMode ? 'bg-white/5' : 'bg-violet-50/50'}`}>
          <div className="text-center">
            <p className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>FIRE Number</p>
            <p className={`text-2xl font-bold ${darkMode ? 'text-orange-400' : 'text-orange-600'}`}>
              {formatCurrency(results.fireNumber)}
            </p>
          </div>

          {/* Progress */}
          <div>
            <div className="flex justify-between mb-1">
              <span className={`text-[10px] ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                {formatCurrency(investedAssets)} of {formatCurrency(results.fireNumber)}
              </span>
              <span className={`text-[10px] font-semibold ${
                results.progress >= 100
                  ? (darkMode ? 'text-emerald-400' : 'text-emerald-600')
                  : (darkMode ? 'text-orange-400' : 'text-orange-600')
              }`}>
                {results.progress.toFixed(1)}%
              </span>
            </div>
            <ProgressBar value={results.progress} max={100} height="sm" customColor={results.progress >= 100 ? '#10b981' : '#f97316'} />
          </div>

          {/* Years to FIRE */}
          <div className="text-center">
            <p className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Years to FIRE</p>
            <p className={`text-lg font-bold ${
              results.yearsToFire === null
                ? (darkMode ? 'text-slate-300' : 'text-slate-700')
                : results.yearsToFire === 0
                ? (darkMode ? 'text-emerald-400' : 'text-emerald-600')
                : results.yearsToFire === -1
                  ? (darkMode ? 'text-red-400' : 'text-red-600')
                  : (darkMode ? 'text-violet-400' : 'text-violet-600')
            }`}>
              {results.yearsToFire === null ? '—' : results.yearsToFire === 0 ? 'FIRE Achieved!' : results.yearsToFire === -1 ? 'N/A' : `${results.yearsToFire} years`}
            </p>
          </div>

          {/* Variants */}
          <div className="grid grid-cols-3 gap-3 text-center">
            <div>
              <p className={`text-[10px] ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Lean FIRE</p>
              <p className={`text-xs font-semibold ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                {formatCurrency(results.leanFire)}
              </p>
              <p className={`text-[10px] ${darkMode ? 'text-slate-600' : 'text-slate-400'}`}>75% expenses</p>
            </div>
            <div>
              <p className={`text-[10px] ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Coast FIRE</p>
              <p className={`text-xs font-semibold ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                {formatCurrency(results.coastFire)}
              </p>
              <p className={`text-[10px] ${darkMode ? 'text-slate-600' : 'text-slate-400'}`}>stop saving now</p>
            </div>
            <div>
              <p className={`text-[10px] ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Fat FIRE</p>
              <p className={`text-xs font-semibold ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                {formatCurrency(results.fatFire)}
              </p>
              <p className={`text-[10px] ${darkMode ? 'text-slate-600' : 'text-slate-400'}`}>150% expenses</p>
            </div>
          </div>
        </div>
        <p className={`text-[10px] ${darkMode ? 'text-slate-600' : 'text-slate-400'}`}>
          Estimates only. Not financial advice. Actual results will vary.
        </p>
      </div>
    </Card>
  );
};
